import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiX, FiBriefcase, FiTool, FiCheck } from "react-icons/fi";
import { login, apiRequest, errorMessage, fetchSiteIdentity } from "../../api";
import defaultLogo from "../../assets/logo.png";

const inputClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light transition-colors";

const defaultTexts = {
  auth_login_title: "Welcome back",
  auth_login_text: "Sign in to manage your jobs and proposals.",
  auth_register_title: "Create your account",
  auth_register_text: "Join as a buyer to hire talent, or as a seller to find work.",
};

// Sign in / register window for the Co-Work marketplace. Texts come from the admin panel
// (Co-Work > Marketplace Content). On success it calls onAuthenticated(user).
export default function AuthModal({ initialMode = "login", initialRole = "buyer", settings, onClose, onAuthenticated }) {
  const navigate = useNavigate();
  const texts = { ...defaultTexts, ...(settings || {}) };
  const [mode, setMode] = useState(initialMode);
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [logoUrl, setLogoUrl] = useState(defaultLogo);

  useEffect(() => {
    fetchSiteIdentity().then((identity) => { if (identity.logo) setLogoUrl(identity.logo); }).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const switchMode = (next) => { setMode(next); setError(""); };

  const signIn = async (email, password) => {
    try {
      await login(email, password);
    } catch {
      throw new Error("Invalid email or password.");
    }
    const { ok, data } = await apiRequest("/core/cowork/me/", { auth: true });
    if (!ok) throw new Error(errorMessage(data));
    if (data.is_staff) {   // an admin signing in here goes to the admin panel
      onClose();
      navigate("/admin/dashboard");
      return;
    }
    onAuthenticated(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "register") {
        const { ok, data } = await apiRequest("/core/cowork/register/", {
          method: "POST",
          body: { full_name: form.full_name, email: form.email, password: form.password, role, phone: form.phone },
        });
        if (!ok) throw new Error(errorMessage(data));
      }
      await signIn(form.email, form.password);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const roleOptions = [
    { key: "buyer", label: "Buyer", hint: "I want to hire talent", Icon: FiBriefcase },
    { key: "seller", label: "Seller", hint: "I want to find work", Icon: FiTool },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md my-auto bg-navy text-white border border-navy-border rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-48 rounded-full bg-accent/30 blur-[80px] pointer-events-none" />

        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white transition-colors"
        >
          <FiX size={20} />
        </button>

        <div className="relative px-7 pt-8 pb-7">
          <div className="flex justify-center mb-5">
            <img src={texts.logo || logoUrl} alt="Logo" className="h-10 w-auto" />
          </div>

          <h3 className="text-2xl font-bold text-center">
            {mode === "login" ? texts.auth_login_title : texts.auth_register_title}
          </h3>
          <p className="text-sm text-gray-400 text-center mt-1 mb-6">
            {mode === "login" ? texts.auth_login_text : texts.auth_register_text}
          </p>

          <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-white/5 border border-navy-border mb-5">
            {[["login", "Sign In"], ["register", "Register"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => switchMode(key)}
                className={`py-2 rounded-md text-sm font-semibold transition-colors ${
                  mode === key ? "bg-brand-gradient text-white shadow" : "text-gray-300 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {roleOptions.map(({ key, label, hint, Icon }) => {
                    const active = role === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setRole(key)}
                        className={`relative text-left p-3 rounded-lg border transition-colors ${
                          active ? "border-accent-light bg-accent/15" : "border-navy-border bg-white/5 hover:border-accent-light/50"
                        }`}
                      >
                        {active && <FiCheck className="absolute top-2 right-2 text-accent-light" size={14} />}
                        <Icon size={18} className={active ? "text-accent-light" : "text-gray-400"} />
                        <p className="font-semibold text-sm mt-1.5">{label}</p>
                        <p className="text-[11px] text-gray-400">{hint}</p>
                      </button>
                    );
                  })}
                </div>
                <input required placeholder="Full name" className={inputClass} value={form.full_name} onChange={set("full_name")} />
              </>
            )}

            <input required type="email" placeholder="Email address" autoComplete="email" className={inputClass} value={form.email} onChange={set("email")} />
            <input
              required
              type="password"
              placeholder={mode === "register" ? "Password (min. 8 characters)" : "Password"}
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              className={inputClass}
              value={form.password}
              onChange={set("password")}
            />
            {mode === "register" && (
              <input placeholder="Phone (optional)" className={inputClass} value={form.phone} onChange={set("phone")} />
            )}

            {error && <p className="text-sm text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={busy}
              className="w-full py-2.5 rounded-lg bg-brand-gradient text-white font-semibold shadow-md shadow-accent/20 disabled:opacity-60"
            >
              {busy ? "Please wait..." : mode === "login" ? "Sign In" : `Create ${role === "buyer" ? "Buyer" : "Seller"} Account`}
            </button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-5">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button type="button" onClick={() => switchMode(mode === "login" ? "register" : "login")} className="text-accent-light hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
